/**
 * AdaptiveHomeDashboard — iter-438k Phase 3 (Unified Adaptive Dashboard).
 * ═══════════════════════════════════════════════════════════════════
 * Renders a dynamic workspace launcher whose contents come exclusively
 * from `GET /api/identity/my-workspaces`. There are NO hardcoded
 * per-role menus — the backend decides what the caller sees.
 *
 * Empty state (caller has zero authorised workspaces) offers a
 * *Request Access* CTA that routes into the existing Admin Requests
 * flow (`/admin/register`) — no parallel workflow.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import * as LucideIcons from 'lucide-react';

import { useAuthStore } from '@/lib/store';
import { getToken } from '@/lib/authToken';
import { DashboardLayout } from '@/components/layouts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Fallback icon when a workspace declares an icon name we don't have
// bundled locally (defensive — the registry uses well-known names).
const Fallback = LucideIcons.LayoutGrid;

const AdaptiveHomeDashboard = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const userType = useAuthStore((s) => s.userType);

  // The admin shell needs an admin session; a plain member opening their own
  // dashboard gets a light container instead (iter 574).
  const Shell = React.useMemo(() => (
    userType === 'admin'
      ? ({ children }) => <DashboardLayout title="Home">{children}</DashboardLayout>
      : ({ children }) => <div className="min-h-screen bg-slate-50 px-4 py-10">{children}</div>
  ), [userType]);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['identity-my-workspaces'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/identity/my-workspaces`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.json();
    },
    staleTime: 30_000,
  });

  // Preferences — separate query so refetches don't reload workspace cards.
  const { data: prefs } = useQuery({
    queryKey: ['identity-preferences'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/identity/preferences`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.json();
    },
    staleTime: 60_000,
  });

  const setPrimary = useMutation({
    mutationFn: async (workspaceKey) => {
      const res = await fetch(`${API_URL}/api/identity/primary-workspace`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${getToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ workspace_key: workspaceKey }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }
      return res.json();
    },
    onSuccess: (_, workspaceKey) => {
      queryClient.invalidateQueries({ queryKey: ['identity-preferences'] });
      toast.success(
        workspaceKey
          ? 'Primary workspace saved. You\u2019ll land here next time you sign in.'
          : 'Primary workspace cleared.'
      );
    },
    onError: (err) => toast.error(err.message || 'Could not save primary workspace'),
  });

  // iter-438k Phase 7 — favourites toggle
  const toggleFavourite = useMutation({
    mutationFn: async (workspaceKey) => {
      const res = await fetch(`${API_URL}/api/identity/favourites`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${getToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ workspace_key: workspaceKey }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['identity-preferences'] });
    },
    onError: (err) => toast.error(err.message || 'Could not update favourites'),
  });

  const primaryKey = prefs?.primary_workspace_key || null;

  // iter 574 — every responsibility this person actually holds, each one a
  // direct way in. Independent of the workspace query so a hiccup in one
  // never blanks the other.
  const { data: mine } = useQuery({
    queryKey: ['me-roles'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/me/roles`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.json();
    },
    staleTime: 30_000,
  });
  const myRoles = mine?.roles || [];
  const favouriteKeys = React.useMemo(
    () => new Set(prefs?.favourites || []),
    [prefs?.favourites],
  );
  const recentKeys = prefs?.recent_workspaces || [];

  const workspaces = data?.workspaces || [];
  const categories = data?.categories || [];

  // iter 621 — "Continue here" activates the appointment server-side first.
  // The backend re-resolves this person's own grants, refuses anything that is
  // not theirs or no longer active, and answers with the organisation-scoped
  // destination. Nothing is derived in the browser, and a refusal shows the
  // reason instead of quietly dropping them on a public page.
  const [openingRole, setOpeningRole] = React.useState(null);
  const [appointmentError, setAppointmentError] = React.useState('');

  const openAppointment = async (role) => {
    const id = role.assignment_id || role.key;
    setOpeningRole(id);
    setAppointmentError('');
    try {
      const res = await fetch(`${API_URL}/api/me/appointments/activate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ assignment_id: id }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || !body?.route) {
        const reason = body?.detail || `The server refused this appointment (HTTP ${res.status}).`;
        setAppointmentError(reason);
        toast.error(reason);
        return;
      }
      queryClient.invalidateQueries({ queryKey: ['active-appointment'] });
      if (body.opens_workspace) {
        // The server has confirmed this session opens an administrative
        // workspace. Record it so the route guard stops disagreeing with the
        // backend and bouncing office holders to the member portal (iter 622).
        useAuthStore.setState((s) => ({ user: { ...(s.user || {}), is_church_admin: true } }));
      }
      navigate(body.route);
    } catch (e) {
      const reason = 'We could not reach the server to open that appointment.';
      setAppointmentError(reason);
      toast.error(reason);
    } finally {
      setOpeningRole(null);
    }
  };

  // Group workspaces by category, preserving the order declared by the
  // backend (`data.categories`) so the layout is stable across users.
  const grouped = React.useMemo(() => {
    const bucket = new Map(categories.map((c) => [c.key, { ...c, items: [] }]));
    for (const ws of workspaces) {
      if (bucket.has(ws.category)) bucket.get(ws.category).items.push(ws);
    }
    return Array.from(bucket.values()).filter((c) => c.items.length > 0);
  }, [workspaces, categories]);

  return (
    <Shell>
      <div className="max-w-7xl mx-auto space-y-8" data-testid="adaptive-home">
        {/* Header */}
        <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
              {greeting()}, {user?.name || user?.email?.split('@')[0] || 'there'}.
            </h1>
            <p className="text-slate-600 mt-1">
              {myRoles.length
                ? `${myRoles.length} role${myRoles.length === 1 ? '' : 's'} across the platform — pick one to carry on.`
                : isLoading ? 'Loading your workspaces…'
                  : workspaces.length
                    ? `${workspaces.length} workspace${workspaces.length === 1 ? '' : 's'} available.`
                    : 'You have no workspaces yet.'}
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => refetch()}
            data-testid="home-refresh-btn"
          >
            <LucideIcons.RefreshCw className="h-4 w-4 mr-2" /> Refresh
          </Button>
        </header>

        {/* iter 574 — your roles and responsibilities */}
        {myRoles.length > 0 && (
          <section data-testid="home-my-roles">
            <div className="flex items-center gap-3 mb-4">
              <h2 className="text-lg font-semibold text-slate-900">Your roles and responsibilities</h2>
              <Badge variant="secondary" className="text-xs" data-testid="home-my-roles-count">
                {myRoles.length}
              </Badge>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {myRoles.map((role) => (
                <RoleCard key={role.key} role={role}
                          busy={openingRole === (role.assignment_id || role.key)}
                          onOpen={() => openAppointment(role)} />
              ))}
            </div>
            {appointmentError && (
              <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-4"
                   data-testid="appointment-denied">
                <p className="text-sm font-medium text-amber-900">
                  That appointment could not be opened
                </p>
                <p className="text-sm text-amber-800 mt-1">{appointmentError}</p>
                <p className="text-xs text-amber-700 mt-2">
                  Pick another appointment above, or ask a super admin to check your access.
                </p>
              </div>
            )}
          </section>
        )}

        {/* Loading skeleton */}
        {isLoading && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="home-loading">
            {[0, 1, 2, 3, 4, 5].map((i) => <Skeleton key={i} className="h-32 rounded-xl" />)}
          </div>
        )}

        {/* Error state — never leaves the user stranded */}
        {isError && (
          <Card data-testid="home-error">
            <CardContent className="py-12 text-center space-y-4">
              <LucideIcons.AlertTriangle className="h-10 w-10 text-red-500 mx-auto" />
              <p className="text-slate-700">We couldn&apos;t load your workspaces just now.</p>
              <Button onClick={() => refetch()}>Try again</Button>
            </CardContent>
          </Card>
        )}

        {/* Empty state — Request Access CTA */}
        {!isLoading && !isError && workspaces.length === 0 && myRoles.length === 0 && (
          <Card className="border-dashed border-2 border-slate-300" data-testid="home-empty">
            <CardContent className="py-16 text-center space-y-4">
              <LucideIcons.LockKeyhole className="h-12 w-12 text-slate-400 mx-auto" />
              <div>
                <h3 className="text-xl font-semibold text-slate-800">No workspaces available yet</h3>
                <p className="text-slate-500 mt-1 max-w-md mx-auto">
                  Your account doesn&apos;t have access to any workspaces. Request access from an administrator and we&apos;ll route it through the existing approval workflow.
                </p>
              </div>
              <Button
                size="lg"
                onClick={() => navigate('/admin/register')}
                data-testid="home-request-access-btn"
              >
                <LucideIcons.Send className="h-4 w-4 mr-2" /> Request Access
              </Button>
            </CardContent>
          </Card>
        )}

        {/* iter-438k Phase 7 — Favourites + Recents quick-access rows */}
        {!isLoading && !isError && workspaces.length > 0 && (
          <QuickAccessRows
            workspaces={workspaces}
            favouriteKeys={favouriteKeys}
            recentKeys={recentKeys}
            onOpen={(ws) => navigate(`/workspace/${ws.key}`)}
          />
        )}

        {/* Categorised workspace cards */}
        {!isLoading && !isError && grouped.map((cat) => (
          <section key={cat.key} data-testid={`home-category-${cat.key}`}>
            <div className="flex items-center gap-3 mb-4">
              <h2 className="text-lg font-semibold text-slate-900">{cat.label}</h2>
              <Badge variant="secondary" className="text-xs">{cat.items.length}</Badge>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {cat.items.map((ws) => (
                <WorkspaceCard
                  key={ws.key}
                  ws={ws}
                  isPrimary={primaryKey === ws.key}
                  isFavourite={favouriteKeys.has(ws.key)}
                  onOpen={() => navigate(`/workspace/${ws.key}`)}
                  onTogglePrimary={() =>
                    setPrimary.mutate(primaryKey === ws.key ? null : ws.key)
                  }
                  onToggleFavourite={() => toggleFavourite.mutate(ws.key)}
                  isSaving={setPrimary.isPending}
                />
              ))}
            </div>
          </section>
        ))}
      </div>
    </Shell>
  );
};

// ─── One responsibility, one way in (iter 574) ──────────────────────
const KIND_STYLE = {
  church: { color: '#7c3aed', label: 'Church' },
  governance: { color: '#0f766e', label: 'Governance' },
  events: { color: '#b45309', label: 'Events' },
  school: { color: '#1d4ed8', label: 'School' },
  operations: { color: '#be123c', label: 'Operations' },
  member: { color: '#475569', label: 'Member' },
};

const RoleCard = ({ role, onOpen, busy = false }) => {
  const Icon = LucideIcons[role.icon] || Fallback;
  const style = KIND_STYLE[role.kind] || { color: '#3b82f6', label: role.kind };
  return (
    <Card
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onOpen()}
      className="cursor-pointer transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5 focus:ring-2 focus:ring-indigo-500 focus:outline-none group"
      data-testid={`role-card-${role.key}`}
      style={{ borderLeft: `3px solid ${style.color}` }}
    >
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between">
          <div
            className="h-11 w-11 rounded-lg flex items-center justify-center"
            style={{ backgroundColor: `${style.color}15` }}
          >
            <Icon className="h-5 w-5" style={{ color: style.color }} />
          </div>
          <Badge variant="outline" className="text-[10px] uppercase tracking-wide">
            {style.label}
          </Badge>
        </div>
        <CardTitle className="text-base mt-3">{role.title}</CardTitle>
      </CardHeader>
      <CardContent className="pt-0 pb-4">
        {role.where && <p className="text-sm font-medium text-slate-700">{role.where}</p>}
        {role.detail && <p className="text-xs text-slate-500 mt-1 line-clamp-2">{role.detail}</p>}
        {role.needs_admin_session && (
          <p className="text-xs text-amber-700 mt-2" data-testid={`role-needs-office-${role.key}`}>
            Opens the administration workspace — needs your office login.
          </p>
        )}
        <div className="flex items-center gap-1 mt-3 text-xs font-medium" style={{ color: style.color }}>
          {busy ? 'Opening…' : 'Continue here'}
          {busy
            ? <LucideIcons.Loader2 className="h-3.5 w-3.5 animate-spin" />
            : <LucideIcons.ArrowRight className="h-3.5 w-3.5 group-hover:translate-x-0.5 transition-transform" />}
        </div>
      </CardContent>
    </Card>
  );
};

// ─── Workspace card ─────────────────────────────────────────────────
const WorkspaceCard = ({ ws, isPrimary, isFavourite, onOpen, onTogglePrimary, onToggleFavourite, isSaving }) => {
  const Icon = LucideIcons[ws.icon] || Fallback;
  return (
    <Card
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onOpen()}
      className={`cursor-pointer transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5 focus:ring-2 focus:ring-indigo-500 focus:outline-none group relative ${
        isPrimary ? 'ring-2 ring-amber-400' : ''
      }`}
      data-testid={`workspace-card-${ws.key}`}
      style={{ borderTop: `3px solid ${ws.color || '#3b82f6'}` }}
    >
      {isPrimary && (
        <Badge
          className="absolute top-2 right-2 bg-amber-500 text-white hover:bg-amber-500 text-xs"
          data-testid={`workspace-card-primary-badge-${ws.key}`}
        >
          <LucideIcons.Star className="h-3 w-3 mr-1 fill-current" /> Primary
        </Badge>
      )}
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between">
          <div
            className="h-11 w-11 rounded-lg flex items-center justify-center"
            style={{ backgroundColor: `${ws.color || '#3b82f6'}15` }}
          >
            <Icon className="h-5 w-5" style={{ color: ws.color || '#3b82f6' }} />
          </div>
          <div className="flex items-center gap-1 mt-1">
            {/* iter-438k Phase 7 — favourite heart toggle */}
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onToggleFavourite && onToggleFavourite(); }}
              aria-label={isFavourite ? 'Remove from favourites' : 'Add to favourites'}
              className={`h-7 w-7 rounded-full flex items-center justify-center transition-colors ${
                isFavourite
                  ? 'text-rose-500 hover:bg-rose-50'
                  : 'text-slate-300 hover:text-rose-500 hover:bg-rose-50'
              }`}
              data-testid={`workspace-card-fav-${ws.key}`}
            >
              <LucideIcons.Heart className={`h-4 w-4 ${isFavourite ? 'fill-current' : ''}`} />
            </button>
            {!isPrimary && (
              <LucideIcons.ArrowUpRight className="h-4 w-4 text-slate-400 group-hover:text-slate-700 transition-colors" />
            )}
          </div>
        </div>
        <CardTitle className="text-base mt-3">{ws.label}</CardTitle>
      </CardHeader>
      <CardContent className="pt-0 pb-4">
        {ws.description && (
          <p className="text-sm text-slate-500 line-clamp-2 mb-3">{ws.description}</p>
        )}
        <Button
          variant="ghost"
          size="sm"
          className="w-full h-8 text-xs text-slate-500 hover:text-amber-600 hover:bg-amber-50"
          onClick={(e) => {
            e.stopPropagation();
            onTogglePrimary();
          }}
          disabled={isSaving}
          data-testid={`workspace-card-pin-${ws.key}`}
        >
          {isPrimary ? (
            <>
              <LucideIcons.StarOff className="h-3.5 w-3.5 mr-1.5" /> Remove as primary
            </>
          ) : (
            <>
              <LucideIcons.Star className="h-3.5 w-3.5 mr-1.5" /> Set as primary
            </>
          )}
        </Button>
      </CardContent>
    </Card>
  );
};

// ─── Quick-access rows (Phase 7 personalisation) ────────────────────
const QuickAccessRows = ({ workspaces, favouriteKeys, recentKeys, onOpen }) => {
  const byKey = React.useMemo(
    () => new Map(workspaces.map((w) => [w.key, w])),
    [workspaces],
  );
  const favourites = React.useMemo(
    () => Array.from(favouriteKeys).map((k) => byKey.get(k)).filter(Boolean),
    [favouriteKeys, byKey],
  );
  const recents = React.useMemo(
    () => recentKeys.map((k) => byKey.get(k)).filter(Boolean).slice(0, 5),
    [recentKeys, byKey],
  );
  if (!favourites.length && !recents.length) return null;

  return (
    <div className="space-y-6" data-testid="home-quick-access">
      {favourites.length > 0 && (
        <QuickRow
          title="Favourites"
          testId="home-favourites"
          icon={LucideIcons.Heart}
          items={favourites}
          onOpen={onOpen}
        />
      )}
      {recents.length > 0 && (
        <QuickRow
          title="Recent"
          testId="home-recents"
          icon={LucideIcons.Clock}
          items={recents}
          onOpen={onOpen}
        />
      )}
    </div>
  );
};

const QuickRow = ({ title, testId, icon: RowIcon, items, onOpen }) => (
  <section data-testid={testId}>
    <div className="flex items-center gap-2 mb-3 text-slate-600">
      <RowIcon className="h-4 w-4" />
      <h3 className="text-sm font-semibold uppercase tracking-wide">{title}</h3>
    </div>
    <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1">
      {items.map((ws) => {
        const Icon = LucideIcons[ws.icon] || Fallback;
        return (
          <button
            key={ws.key}
            type="button"
            onClick={() => onOpen(ws)}
            className="flex-shrink-0 flex items-center gap-2 px-3 py-2 bg-white border border-slate-200 rounded-lg hover:border-slate-400 hover:shadow-sm transition-all"
            data-testid={`${testId}-item-${ws.key}`}
          >
            <div
              className="h-6 w-6 rounded flex items-center justify-center"
              style={{ backgroundColor: `${ws.color || '#3b82f6'}20` }}
            >
              <Icon className="h-3.5 w-3.5" style={{ color: ws.color || '#3b82f6' }} />
            </div>
            <span className="text-sm font-medium text-slate-800">{ws.label}</span>
          </button>
        );
      })}
    </div>
  </section>
);

// ─── Helpers ────────────────────────────────────────────────────────
const greeting = () => {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
};

export default AdaptiveHomeDashboard;
